import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import vendorApi from '../services/vendorApi';

const VendorConsignments = () => {
  const navigate = useNavigate();
  const [consignments, setConsignments] = useState([]);
  const [filteredConsignments, setFilteredConsignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadConsignments();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredConsignments(consignments);
    } else {
      setFilteredConsignments(consignments.filter(c => c.status === statusFilter));
    }
  }, [statusFilter, consignments]);

  const loadConsignments = async () => {
    try {
      const data = await vendorApi.getConsignments();
      setConsignments(data);
      setFilteredConsignments(data);
    } catch (err) {
      setError('Failed to load consignments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      sold: 'success',
      returned: 'info',
      expired: 'error'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const statusCounts = {
    all: consignments.length,
    pending: consignments.filter(c => c.status === 'pending').length,
    sold: consignments.filter(c => c.status === 'sold').length,
    returned: consignments.filter(c => c.status === 'returned').length
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        My Consignments
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Track your consigned items and their status
      </Typography>

      <Card>
        <Tabs
          value={statusFilter}
          onChange={(e, newValue) => setStatusFilter(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`All (${statusCounts.all})`} value="all" />
          <Tab label={`Pending (${statusCounts.pending})`} value="pending" />
          <Tab label={`Sold (${statusCounts.sold})`} value="sold" />
          <Tab label={`Returned (${statusCounts.returned})`} value="returned" />
        </Tabs>

        <CardContent>
          {filteredConsignments.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Consignment Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Agreed Price</TableCell>
                    <TableCell align="right">Sale Price</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell align="right">Payout</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredConsignments.map((consignment) => {
                    const salePrice = consignment.actual_sale_price || consignment.agreed_price;
                    const commission = salePrice * (consignment.commission_rate / 100);
                    const payout = consignment.payout_amount || (salePrice - commission);

                    return (
                      <TableRow
                        key={consignment.id}
                        hover
                        onClick={() => navigate(`/vendor/consignments/${consignment.id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {consignment.item?.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(consignment.consignment_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={consignment.status}
                            color={getStatusColor(consignment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          ${consignment.agreed_price?.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {consignment.actual_sale_price
                            ? `$${consignment.actual_sale_price.toFixed(2)}`
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {consignment.commission_rate}%
                        </TableCell>
                        <TableCell align="right">
                          {consignment.status === 'sold'
                            ? `$${payout.toFixed(2)}`
                            : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              No consignments found
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VendorConsignments;
